/**
 * Product-wide SWR surface registry — bootstrap GET keys for every major mode.
 *
 * One entry per product surface: path prefixes (for enter-surface preload),
 * URL persist prefixes (localStorage across reloads), and optional preload
 * loaders that fill the shared SWR map on first enter.
 *
 * Only **bootstrap** GETs (page-level payloads), never infinite feeds or
 * param-heavy detail routes (would thrash the 4MB localStorage budget).
 *
 * Pure data + thin loaders; React mounts live in swr-provider.tsx.
 */

import { apiCall, apiKey, type Args, type Operation } from "@excellent/ui/api";
import { agentsViews } from "@excellent/ui/api/contracts/agents-views";
import { agentAnalytics } from "@excellent/ui/api/contracts/agent-analytics";
import { agentHealth } from "@excellent/ui/api/contracts/agent-health";
import { verificationOps } from "@excellent/ui/api/contracts/verification-ops";
import { claimsLanes } from "@excellent/ui/api/contracts/claims-lanes";
import { triageBoard } from "@excellent/ui/api/contracts/triage-board";
import { agentGoals } from "@excellent/ui/api/contracts/agent-goals";
import { agentRunsFeed } from "@excellent/ui/api/contracts/agent-runs";
import { ratchetLedgerRead } from "@excellent/ui/api/contracts/ratchet-ledger";
import { workflowFleet } from "@excellent/ui/api/contracts/workflows/fleet";
import { loopsRunnability } from "@excellent/ui/api/contracts/workflows/runnability";
import { workflowRoadmap } from "@excellent/ui/api/contracts/workflows/roadmap";
import { qa } from "@excellent/ui/api/contracts/qa";
import { lens } from "@excellent/ui/api/contracts/lens";
import { customers } from "@excellent/ui/api/contracts/customers";
import { crm } from "@excellent/ui/api/contracts/crm";
import { hiringViews } from "@excellent/ui/api/contracts/hiring-views";
import { projectsViews } from "@excellent/ui/api/contracts/projects-views";
import { product } from "@excellent/ui/api/contracts/product";
import { operations } from "@excellent/ui/api/contracts/operations";
import { market } from "@excellent/ui/api/contracts/market";
import { onboarding } from "@excellent/ui/api/contracts/onboarding";
import { userSettings } from "@excellent/ui/api/contracts/user-settings";
import { schema } from "@excellent/ui/api/contracts/database/schema";
import { objectTypes } from "@excellent/ui/api/contracts/database/object-types";
import { favorites } from "@excellent/ui/api/contracts/favorites";
import { play } from "@excellent/ui/api/contracts/play";
import { suggestions } from "@excellent/ui/api/contracts/suggestions";
import { homePortfolio } from "@excellent/ui/api/contracts/home-portfolio";
import { modes } from "@excellent/ui/api/contracts/modes";
import { telemetry } from "@excellent/ui/api/contracts/telemetry";
import { shell } from "@excellent/ui/api/contracts/shell";

export type PreloadEntry = {
  key: string;
  load: () => Promise<unknown>;
};

export type ProductSurface = {
  /** Stable id for tests / dumps. */
  id: string;
  /** Pathname prefixes that "enter" this surface (first match wins for preload). */
  pathPrefixes: readonly string[];
  /** URL prefixes persisted to localStorage (query variants match via startsWith). */
  persistPrefixes: readonly string[];
  /** Bootstrap loads fired once when the user first hits a path prefix this session. */
  preload: readonly PreloadEntry[];
};

function preload(
  key: string,
  load: () => Promise<unknown>,
): PreloadEntry {
  return { key, load };
}

// These mirror `apiKey`'s own generic signature rather than deriving it via
// `Parameters<typeof apiKey>[0]`. That derivation collapses the type params to
// `Operation<unknown, unknown, unknown, unknown>`, and `Operation` is invariant
// in `TParams` through `path: (params: TParams) => string` — so no concrete
// contract is assignable to it and every call site fails TS2345.
function keyOf<TParams, TQuery, TBody, TResult>(
  op: Operation<TParams, TQuery, TBody, TResult>,
  args: Args<TParams, TQuery, TBody> = {} as Args<TParams, TQuery, TBody>,
): string {
  return apiKey(op, args);
}

function loadOf<TParams, TQuery, TBody, TResult>(
  op: Operation<TParams, TQuery, TBody, TResult>,
  args: Args<TParams, TQuery, TBody> = {} as Args<TParams, TQuery, TBody>,
): () => Promise<unknown> {
  return () => apiCall(op, args);
}

function entry<TParams, TQuery, TBody, TResult>(
  op: Operation<TParams, TQuery, TBody, TResult>,
  args: Args<TParams, TQuery, TBody> = {} as Args<TParams, TQuery, TBody>,
): PreloadEntry {
  return preload(keyOf(op, args), loadOf(op, args));
}

/**
 * All major product surfaces. Order matters only for first-match path preload.
 * More specific path prefixes should appear before shorter ones when needed.
 */
export const PRODUCT_SWR_SURFACES: readonly ProductSurface[] = [
  {
    id: "agents",
    pathPrefixes: ["/agents"],
    persistPrefixes: [
      keyOf(agentsViews.overview, { query: {} }),
      keyOf(agentsViews.runsBootstrap, {}),
      "/api/agents/analytics",
      "/api/agents/analytics/you",
      keyOf(agentHealth.get, {}),
      keyOf(verificationOps.get, {}),
      keyOf(claimsLanes.get, {}),
      keyOf(triageBoard.list, { query: { limit: 100 } }),
      keyOf(ratchetLedgerRead, {}),
      "/api/agents/runs",
      keyOf(agentGoals.list, { query: {} }),
      keyOf(agentGoals.roles, {}),
      "/api/workflows/fleet",
      keyOf(loopsRunnability.get, {}),
      keyOf(workflowRoadmap.get, {}),
      keyOf(qa.runsList, { query: {} }),
      keyOf(qa.suitesList, {}),
      // Lens (Agents ▸ Analytics ▸ You)
      keyOf(lens.version, {}),
      keyOf(lens.snapshot, {}),
      keyOf(lens.settings, {}),
      keyOf(lens.history, { query: { limit: 2000 } }),
      "/lens/api/history",
      keyOf(lens.todos, {}),
      keyOf(lens.plans, {}),
      keyOf(lens.memoryList, {}),
    ],
    preload: [
      entry(agentsViews.overview, { query: {} }),
      entry(agentAnalytics.page, { query: {} }),
      entry(agentHealth.get, {}),
      entry(ratchetLedgerRead, {}),
      entry(agentGoals.list, { query: {} }),
      entry(agentGoals.roles, {}),
      entry(workflowFleet.list, { query: {} }),
      entry(loopsRunnability.get, {}),
      entry(lens.snapshot, {}),
    ],
  },
  {
    id: "customers",
    pathPrefixes: ["/customers"],
    persistPrefixes: [
      keyOf(customers.dashboard, {}),
      keyOf(customers.portfolio, {}),
      keyOf(customers.forecast, {}),
      keyOf(customers.board, { query: {} }),
      "/api/customers/",
      // CRM views nested under customers mode
      keyOf(crm.views.accounts, {}),
      keyOf(crm.views.contacts, {}),
      keyOf(crm.views.forecast, {}),
      keyOf(crm.views.renewal, {}),
      keyOf(crm.views.sequences, {}),
      keyOf(crm.views.leads, {}),
      keyOf(crm.pipelines.list, {}),
      keyOf(crm.leads.list, { query: {} }),
      keyOf(crm.opportunities.list, { query: {} }),
      keyOf(crm.templates.list, {}),
      keyOf(crm.sequences.list, {}),
      "/api/crm/",
    ],
    preload: [
      entry(customers.dashboard, {}),
      entry(crm.views.accounts, {}),
      entry(crm.pipelines.list, {}),
    ],
  },
  {
    id: "hiring",
    pathPrefixes: ["/hiring"],
    persistPrefixes: [
      keyOf(hiringViews.jobs, { query: {} }),
      keyOf(hiringViews.candidates, { query: {} }),
      keyOf(hiringViews.pipelineHub, {}),
      keyOf(hiringViews.settings, {}),
      "/hiring/api/views/",
      "/hiring/api/jobs",
      "/hiring/api/pipelines",
      "/hiring/api/candidates",
    ],
    preload: [
      entry(hiringViews.jobs, { query: {} }),
      entry(hiringViews.pipelineHub, {}),
      entry(hiringViews.candidates, { query: {} }),
    ],
  },
  {
    id: "projects",
    pathPrefixes: ["/projects"],
    persistPrefixes: [
      keyOf(projectsViews.catalog, {}),
      keyOf(projectsViews.map, {}),
      "/api/projects/catalog",
      "/api/projects/map",
      "/api/projects/by-slug/",
      "/projects/api/tree",
      "/projects/api/tasks",
    ],
    preload: [
      entry(projectsViews.catalog, {}),
      entry(projectsViews.map, {}),
    ],
  },
  {
    id: "work",
    pathPrefixes: ["/work", "/issues", "/planner", "/activity", "/favorites", "/suggestions", "/verdicts"],
    persistPrefixes: [
      keyOf(modes.workDashboard, { query: {} }),
      keyOf(modes.planner, { query: {} }),
      keyOf(modes.workInbox, {}),
      keyOf(modes.workProjects, {}),
      keyOf(modes.issuesBoard, { query: {} }),
      keyOf(favorites.list, {}),
      keyOf(play.queue, {}),
      keyOf(suggestions.list, {}),
      "/api/modes/",
      "/api/favorites",
      "/api/play/queue",
      "/api/suggestions",
      "/api/activity",
    ],
    preload: [
      entry(modes.workDashboard, { query: {} }),
      entry(play.queue, {}),
      entry(suggestions.list, {}),
      entry(favorites.list, {}),
    ],
  },
  {
    id: "product",
    pathPrefixes: ["/product"],
    persistPrefixes: [
      keyOf(product.dashboard, {}),
      keyOf(product.properties, {}),
      keyOf(product.kindOptions, {}),
      "/api/product/",
    ],
    preload: [entry(product.dashboard, {})],
  },
  {
    id: "operations",
    pathPrefixes: ["/operations"],
    persistPrefixes: [
      keyOf(operations.dashboard, {}),
      "/api/operations/",
    ],
    preload: [entry(operations.dashboard, {})],
  },
  {
    id: "market",
    pathPrefixes: ["/market"],
    persistPrefixes: [
      keyOf(market.page, {}),
      "/api/market/",
    ],
    preload: [entry(market.page, {})],
  },
  {
    id: "database",
    pathPrefixes: ["/database"],
    persistPrefixes: [
      keyOf(schema.objects, {}),
      keyOf(schema.connection, {}),
      keyOf(schema.relationships, {}),
      keyOf(schema.migrations, {}),
      keyOf(objectTypes.list, {}),
      keyOf(objectTypes.kinds, {}),
      "/api/database/",
    ],
    preload: [
      entry(schema.objects, {}),
      entry(schema.connection, {}),
      entry(objectTypes.list, {}),
    ],
  },
  {
    id: "settings",
    pathPrefixes: ["/settings"],
    persistPrefixes: [
      keyOf(userSettings.getWorkspace, {}),
      keyOf(userSettings.getProfile, {}),
      keyOf(userSettings.getPreferences, {}),
      keyOf(userSettings.getNotifications, {}),
      keyOf(userSettings.getAppearance, {}),
      "/api/settings/",
      "/api/settings/agents/pages/",
    ],
    preload: [
      entry(userSettings.getWorkspace, {}),
      entry(userSettings.getProfile, {}),
      entry(userSettings.getAppearance, {}),
    ],
  },
  {
    id: "welcome",
    pathPrefixes: ["/welcome"],
    persistPrefixes: [
      keyOf(onboarding.get, {}),
      "/api/onboarding",
    ],
    preload: [entry(onboarding.get, {})],
  },
  {
    id: "home",
    // Mode root / and common home chrome — path "" is handled specially
    pathPrefixes: ["/", "/home"],
    persistPrefixes: [
      keyOf(shell.get, { query: {} }),
      keyOf(play.queue, {}),
      keyOf(suggestions.list, {}),
      keyOf(onboarding.get, {}),
      keyOf(homePortfolio.portfolio, { query: {} }),
      "/api/home/",
      "/api/shell",
      "/api/play/queue",
      "/api/suggestions",
    ],
    preload: [
      entry(play.queue, {}),
      entry(suggestions.list, {}),
      entry(onboarding.get, {}),
    ],
  },
  {
    id: "transparency",
    pathPrefixes: ["/transparency"],
    persistPrefixes: [
      keyOf(telemetry.disclosure, {}),
      "/api/telemetry/",
    ],
    preload: [entry(telemetry.disclosure, {})],
  },
];

/** Flat unique persist prefixes for the shell localStorage provider. */
export function allPersistPrefixes(
  surfaces: readonly ProductSurface[] = PRODUCT_SWR_SURFACES,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const s of surfaces) {
    for (const p of s.persistPrefixes) {
      if (seen.has(p)) continue;
      seen.add(p);
      out.push(p);
    }
  }
  return out;
}

/**
 * True when a cache key should survive reload under the given prefixes.
 * Query variants match (`/api/foo?x=1` under `/api/foo`).
 */
export function shouldPersistKey(key: string, prefixes: readonly string[]): boolean {
  for (const p of prefixes) {
    if (key === p || key.startsWith(p + "?") || key.startsWith(p + "/")) return true;
  }
  return false;
}

/** First surface whose pathPrefixes match pathname (home `/` is last-resort exact). */
export function surfaceForPath(
  pathname: string,
  surfaces: readonly ProductSurface[] = PRODUCT_SWR_SURFACES,
): ProductSurface | null {
  const path = pathname.split("?")[0] || "/";
  for (const s of surfaces) {
    if (s.id === "home") continue; // evaluate home last
    for (const pref of s.pathPrefixes) {
      if (path === pref || path.startsWith(pref + "/")) return s;
    }
  }
  // Home: exact / or /home*
  for (const s of surfaces) {
    if (s.id !== "home") continue;
    if (path === "/" || path === "/home" || path.startsWith("/home/")) return s;
  }
  return null;
}

/** Dump for inventory / tests (no loaders). */
export function dumpSurfaceRegistry(
  surfaces: readonly ProductSurface[] = PRODUCT_SWR_SURFACES,
): Array<{
  id: string;
  pathPrefixes: string[];
  persistPrefixes: string[];
  preloadKeys: string[];
}> {
  return surfaces.map((s) => ({
    id: s.id,
    pathPrefixes: [...s.pathPrefixes],
    persistPrefixes: [...s.persistPrefixes],
    preloadKeys: s.preload.map((p) => p.key),
  }));
}
